import { useMemo } from 'react';
import { useRouter } from 'next/compat/router';
import _ from 'lodash';

import { useFlags } from 'launchdarkly-react-client-sdk';

import useUI from 'hooks/use-ui';
import useDispensaryDeliveryInfo from 'dispensary/hooks/use-dispensary-delivery-info';
import { getLogger } from 'src/utils/datadog-logger';

import { consumerDispensaries } from 'shared/graphql/dispensary/queries';
import { useQuery } from '@apollo/client';

const logger = getLogger('use-dispensary');

export const getQuery = () => consumerDispensaries;

export const getVariables = (dispensaryId, filters) => ({
  dispensaryFilter: {
    cNameOrID: dispensaryId,
    ...filters,
  },
});

export const getDispensary = async (apolloClient, props) =>
  apolloClient.query({
    query: getQuery(),
    variables: getVariables(props.params.cName),
    skip: !props.params.cName,
  });

function getError(query) {
  // eslint-disable-next-line lodash/prefer-lodash-method
  const params = Object.keys(query).join(', ');
  const error = new Error(
    `useDispensary may only be called from a route with a cName parameter. Found keys: ${params}`
  );
  error.query = query;

  return error;
}

export default function useDispensary(id) {
  const { isFirstRender } = useUI();
  const { isReady, query } = useRouter() ?? {};
  const flags = useFlags();
  const disableHeadFallback = flags['core.ecomm.disable-dispensary-head-fallback.rollout'] ?? false;

  const { cName, dispensaryId: dispoIdFromQuery, cname } = query ?? {};
  /**
   * Context for different cname identifier params:
   * - cName: The canonical dispensary name used in the main URL structure
   * - cname: Alternative lowercase variant used specifically in pay/signup routes query params
   * We need to support both casing variants (cName and cname) to ensure all route patterns
   * can correctly identify the dispensary regardless of which URL pattern was used to access it.
   */
  const dispensaryId = id || cName || dispoIdFromQuery || cname;

  const { data, loading, error, ...apolloValues } = useQuery(consumerDispensaries, {
    variables: getVariables(dispensaryId),
    skip: isFirstRender || !isReady || !dispensaryId,
  });

  const matchedDispensary = _.find(data?.filteredDispensaries, (d) => d.cName === cName || d.id === dispensaryId);

  // ECOMM-9255: If the API returned results but none matched, the previous _.head() fallback
  // silently selected the first dispensary in the list, causing orders on chain storefronts to
  // be routed to the wrong location (e.g., Ashland instead of the customer's selected store).
  // When the flag is enabled, return undefined instead of guessing.
  let dispensary = matchedDispensary;
  if (!matchedDispensary && data?.filteredDispensaries?.length > 0) {
    logger.error('dispensary_lookup_mismatch', {
      dispensaryId: dispensaryId || 'none',
      cName: cName || 'none',
      resultCount: data.filteredDispensaries.length,
      resultCNames: data.filteredDispensaries.map((d) => d.cName).join(','),
    });

    if (!disableHeadFallback) {
      dispensary = _.head(data?.filteredDispensaries);
    }
  }

  // Only fetch delivery info if we have the actual dispensary object with its numeric ID
  // dispensaryId from router might be a cName string like 'dr-jollys', but API needs numeric ID
  const actualDispensaryId = dispensary?.id;
  const deliveryInfoResult = useDispensaryDeliveryInfo(actualDispensaryId || null);

  const finalDispensary = useMemo(() => {
    if (!deliveryInfoResult || !dispensary) {
      return dispensary;
    }
    return {
      ...dispensary,
      deliveryInfo: deliveryInfoResult.deliveryInfo || dispensary.deliveryInfo,
      taxConfig: {
        ...dispensary.taxConfig,
        taxes: deliveryInfoResult.taxes || dispensary.taxConfig.taxes,
      },
    };
  }, [dispensary, deliveryInfoResult]);

  if (isReady && !dispensaryId) {
    return {
      data,
      loading,
      error: getError(query),
      dispensary: finalDispensary,
      ...apolloValues,
    };
  }

  return {
    data,
    loading: isFirstRender || !isReady || loading,
    error,
    dispensary: finalDispensary,
    dispensaryId,
    ...apolloValues,
  };
}
